import { Wildfire } from 'features/wildfires/wildfire.types';
import { SubmittedWildfireEditData } from 'features/provisionalSubmissions/provisionalSubmissions.types';
import {
  BotLockableField,
  WildfireGeoEventData,
} from 'features/wildfires/wildfireGeoEventData.types';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { getMatchingRegions } from 'features/geoEvents/geoEvent.utils';
import { hasRegionsChanged } from 'features/geoEvents/hooks/useGeoEventGeolocationFormFieldChanges/useGeoEventGeolocationFormFieldChanges.utils';
import { union } from 'es-toolkit';
import { ReporterNotesFormValues } from 'features/reporterNotes/reporterNotes.types';
import {
  buildReporterNotesPayload,
  reporterNotesChangesToFormValues,
} from 'features/reporterNotes/reporterNotes.utils';
import { Region } from 'shared/types';

export type Status = 'inactive' | 'active';

export type FormValues = ReporterNotesFormValues & {
  status: Status;
  reporterManaged: boolean;
  reporterOnlyNotes: string;
  address: string;
  regions: GeoEventRegion[];
  lat: number;
  lng: number;
  confirmAdditionalRegions?: boolean;
  confirmRemovedRegions?: boolean;
};

type MatchingRegionsResult = {
  placeName: string;
  matchingRegions: Region[];
};

const BOT_LOCKED_LOCATION_FIELDS: BotLockableField[] = ['lat', 'lng'];

export const convertSubmittedDataToFormValues = (
  submittedData: SubmittedWildfireEditData,
  geoEvent: Wildfire,
): FormValues => ({
  status: submittedData.isActive ? 'active' : 'inactive',
  reporterManaged: submittedData.reporterManaged ?? geoEvent.reporterManaged,
  reporterOnlyNotes: submittedData.data?.reporterOnlyNotes || '',
  address: submittedData.address ?? geoEvent.address ?? '',
  regions: submittedData.regions ?? geoEvent.regions,
  lat: submittedData.lat ?? geoEvent.lat,
  lng: submittedData.lng ?? geoEvent.lng,
  ...reporterNotesChangesToFormValues(submittedData.reporterNotes),
});

const hasLocationMoved = (geoEvent: Wildfire, values: FormValues): boolean =>
  geoEvent.lat !== values.lat || geoEvent.lng !== values.lng;

// The form seeds a missing address as '', so normalize before comparing.
const hasAddressChanged = (geoEvent: Wildfire, values: FormValues): boolean =>
  (geoEvent.address ?? '').trim() !== values.address.trim();

export const buildEditedGeoEventData = (
  geoEvent: Wildfire,
  values: FormValues,
): WildfireGeoEventData => ({
  ...geoEvent.data,
  reporterOnlyNotes: values.reporterOnlyNotes,
  botLockedFields: hasLocationMoved(geoEvent, values)
    ? union(geoEvent.data.botLockedFields ?? [], BOT_LOCKED_LOCATION_FIELDS)
    : geoEvent.data.botLockedFields,
});

/**
 * Location and address fields are sent only when they differ from `geoEvent`,
 * so a stale copy can't revert a bot update on approval.
 */
export const buildProvisionalEditData = (
  geoEvent: Wildfire,
  values: FormValues,
): SubmittedWildfireEditData => ({
  isActive: values.status === 'active',
  name: geoEvent.name,
  ...(hasLocationMoved(geoEvent, values) && {
    lat: values.lat,
    lng: values.lng,
  }),
  ...(hasAddressChanged(geoEvent, values) && { address: values.address }),
  geoEventType: 'wildfire',
  notificationType: geoEvent.notificationType,
  ...(hasRegionsChanged(geoEvent.regions, values.regions) && {
    regions: values.regions,
  }),
  reporterManaged: values.reporterManaged,
  data: buildEditedGeoEventData(geoEvent, values),
  evacZoneStatuses: (geoEvent.evacZoneStatuses ?? []).map((ezs) => ({
    status: ezs.status,
    evacZone: { id: ezs.evacZone.id },
  })),
  childGeoEvents: (geoEvent.childGeoEvents ?? []).map((ge) => ({
    id: ge.id,
    geoEventType: ge.geoEventType,
  })),
  reporterNotes: buildReporterNotesPayload(values),
});

/**
 * Reverse-geocode cache keyed by `lat,lng,geoEventType`. The reverse-geocode
 * network call is the expensive part, and this is only reached by the Reporter
 * Managed toggle seeding the Address field, so a reporter flipping the toggle
 * on/off repeatedly at one marker position should only ever trigger one lookup
 * per coordinate. We cache the in-flight promise (not just the resolved value)
 * so concurrent toggles de-dupe too.
 *
 * `regionOptions` is intentionally excluded from the key: it's loaded once per
 * session via `useUserRegionsQuery` and is effectively stable, so caching on
 * coordinates alone is safe.
 */
const matchingRegionsCache = new Map<string, Promise<MatchingRegionsResult>>();

export const getMatchingRegionsMemoized = (
  lat: number,
  lng: number,
  regionOptions: Region[],
  geoEventType: GeoEventType,
): Promise<MatchingRegionsResult> => {
  const key = `${lat},${lng},${geoEventType}`;
  const cached = matchingRegionsCache.get(key);
  if (cached) {
    return cached;
  }

  const promise = getMatchingRegions(
    lat,
    lng,
    regionOptions,
    geoEventType,
  ).catch((error) => {
    // Don't cache failures, so a later toggle can retry the lookup.
    matchingRegionsCache.delete(key);
    throw error;
  });

  matchingRegionsCache.set(key, promise);
  return promise;
};

// Exposed for tests to reset module-level cache between cases.
export const clearMatchingRegionsCache = (): void => {
  matchingRegionsCache.clear();
};
